import {
  Alert,
  Badge,
  Button,
  Flex,
  Heading,
  IconArrowStartLine,
  Spinner,
  Tabs,
  Text,
  View,
} from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  ERROR,
  SUCCESS,
  useNotification,
} from "../../components/CustomHooks/NotificationProvider";
import { AssessmentPlanService } from "../../services/AssessmentService";
import { auth } from "../../utils/auth";
import {
  EXTERNAL_MAIN_MIN_HEIGHT,
  MAIN_MIN_HEIGHT,
} from "../../utils/constants";
import AuthoringAndReview from "./AuthoringAndReview/AuthoringAndReview";
import DataCollectionStatus from "./DataCollectionStatus/DataCollectionStatus";
import MeetingAgenda from "./MeetingAgenda/MeetingAgenda";

const AssessmentPlanningDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams();
  const showNotification = useNotification();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [plan, setPlan] = useState(location.state?.plan || null);
  const [loading, setLoading] = useState(!location.state?.plan);
  const [groupBy, setGroupBy] = useState("section");

  const initialAgenda = {
    plan_id: planId,
    name: "",
    meeting_date: new Date().toLocaleDateString("en-CA"),
    items: [],
    group_order: { mode: "section", order: [] },
    updated_by: "",
  };
  const [agendaState, setAgendaState] = useState(initialAgenda);

  useEffect(() => {
    // Skip fetch if plan was passed via location state
    if (location.state?.plan) return;
    if (planId) {
      const controller = new AbortController();
      setLoading(true);
      AssessmentPlanService.getAssessmentPlanById(planId, controller.signal)
        .then((fullPlan) => {
          setPlan(fullPlan);
        })
        .catch((err) => {
          if (err?.name === "AbortError") return;
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("assessment.assessmentPlan"),
            }),
            ERROR
          );
        })
        .finally(() => setLoading(false));
      return () => controller.abort();
    }
  }, [planId, showNotification, t, location.state?.plan]);

  const handleTabChange = (_e, { index }) => {
    setSelectedIndex(index);
  };

  const handleAddToAgenda = (items) => {
    let addedCount = 0;
    setAgendaState((prev) => {
      const existingIds = new Set(
        (prev.items || []).map((i) => `${i.source_id}_${i.source_type}`)
      );
      const newItems = items.filter(
        (item) => !existingIds.has(`${item.sourceId}_${item.sourceType}`)
      );
      addedCount = newItems.length;
      if (newItems.length === 0) {
        return prev;
      }
      const startSequence = (prev.items || []).length;
      const mappedItems = newItems.map((item, index) => ({
        item_id: crypto.randomUUID(),
        source_id: item.sourceId,
        source_type: item.sourceType,
        section_name: item.sectionName,
        item_name: item.itemName,
        goal_id: item.goalId || null,
        goal_description: item.goalDescription || "",
        objective_id: item.objectiveId || null,
        objective_description: item.objectiveDescription || "",
        notes: null,
        sequence: startSequence + index,
      }));
      return { ...prev, items: [...(prev.items || []), ...mappedItems] };
    });
    // Use setTimeout to read addedCount after state updater runs synchronously
    setTimeout(() => {
      if (addedCount > 0) {
        showNotification(
          t("assessmentPlanningDashboard.meetingAgenda.itemsAdded", {
            count: addedCount,
          }),
          SUCCESS
        );
      } else {
        showNotification(
          t("assessmentPlanningDashboard.meetingAgenda.itemsAlreadyExist"),
          SUCCESS
        );
      }
    }, 0);
  };

  const handleGoBack = () => {
    if (location.state?.resultsState) {
      navigate("/results", { state: location.state.resultsState });
    } else {
      navigate(-1);
    }
  };

  return (
    <div
      style={{
        margin: "-0.75rem",
        height: auth ? MAIN_MIN_HEIGHT : EXTERNAL_MAIN_MIN_HEIGHT,
        overflow: "hidden",
      }}
    >
      <Flex direction="column" height="100%">
        <Flex.Item padding="small">
          <View as="div" borderWidth="0 0 small 0" margin="0 0 small 0">
            {!plan ? (
              <Heading level="h2" as="h1">
                {t("global.loading") + "..."}
              </Heading>
            ) : (
              <Flex wrap="wrap" justifyItems="space-between" gap="x-small">
                <Flex.Item>
                  <Heading level="h2" as="h1">
                    {plan.name || plan.cycle.name}
                  </Heading>
                </Flex.Item>
                <Flex.Item>
                  <Text>
                    {t("cyclesAndGoals.cycle")}: {plan.cycle.name}
                  </Text>
                </Flex.Item>
              </Flex>
            )}
          </View>
        </Flex.Item>
        <Flex.Item shouldGrow shouldShrink overflowY="auto" padding="0 small">
          {loading ? (
            <View as="div" textAlign="center" padding="large">
              <Spinner renderTitle={t("global.loading")} />
            </View>
          ) : !plan ? (
            <Alert variant="warning">
              {t("assessmentPlanningDashboard.planNotFound")}
            </Alert>
          ) : (
            <Tabs onRequestTabChange={handleTabChange}>
              <Tabs.Panel
                id="authoring-and-review"
                renderTitle={t(
                  "assessmentPlanningDashboard.tabs.authoringAndReview"
                )}
                isSelected={selectedIndex === 0}
              >
                <AuthoringAndReview
                  plan={plan}
                  onAddToAgenda={handleAddToAgenda}
                  agenda={agendaState}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                />
              </Tabs.Panel>
              <Tabs.Panel
                id="data-collection-status"
                renderTitle={t(
                  "assessmentPlanningDashboard.tabs.dataCollectionStatus"
                )}
                isSelected={selectedIndex === 1}
              >
                <DataCollectionStatus
                  plan={plan}
                  onAddToAgenda={handleAddToAgenda}
                  agenda={agendaState}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                />
              </Tabs.Panel>
              <Tabs.Panel
                id="meeting-agenda"
                renderTitle={
                  <Badge count={agendaState.items.length} countUntil={100}>
                    <span style={{ paddingRight: "12px" }}>
                      {t("assessmentPlanningDashboard.tabs.meetingAgenda")}
                    </span>
                  </Badge>
                }
                isSelected={selectedIndex === 2}
              >
                <MeetingAgenda
                  plan={plan}
                  agenda={agendaState}
                  setAgenda={setAgendaState}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                  initialAgenda={initialAgenda}
                />
              </Tabs.Panel>
            </Tabs>
          )}
        </Flex.Item>
        <Flex.Item padding="small">
          <View as="div" borderWidth="small 0 0 0">
            <Button
              renderIcon={IconArrowStartLine}
              onClick={handleGoBack}
              margin="small 0 0 0"
            >
              {t("global.goBack")}
            </Button>
          </View>
        </Flex.Item>
      </Flex>
    </div>
  );
};

export default AssessmentPlanningDashboard;
